import React, { useState, useEffect } from 'react';
import { Breadcrumb, Card, Layout, Typography, Tooltip, Spin } from 'antd';
import { useNavigate, useLocation } from "react-router-dom";
import { useMsal } from '@azure/msal-react';
import axios from 'axios';
import { HomeOutlined } from '@ant-design/icons';
import AppHeaderHomePageTemplate from '../Organism/AppHeaderHomePageTemplate';
import { ActionCardCatagories } from '../Atoms/ActionCardCatagories';
import { loginRequest } from '../../authConfig';
import { Modal, Button } from 'antd'; // Import Modal and Button from Ant Design
import { useTranslation } from 'react-i18next';

const { Content } = Layout;

interface CardType {
  icon: JSX.Element;
  title: string;
  details: any;
  isEnabled?: boolean;
}

const AwardsPage: React.FC = () => {
  const [cards, setCards] = useState<CardType[]>([]);
  const [selectedSection, setSelectedSection] = useState('awards');
  const [userRoles, setUserRoles] = useState<string[]>([]);
  const [nominations, setNominations] = useState<any[]>([]); // State for nominations
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalContent, setModalContent] = useState<string>('');
  // True while fetching child rewards on a card click — shows a spinner instead
  // of a frozen page and blocks double navigation.
  const [navigating, setNavigating] = useState(false);
  const { t } = useTranslation();

  const location = useLocation();
  const navigate = useNavigate();

  const { instance, accounts } = useMsal();

  const rewardDetails = location.state?.rewardDetails || [];
  const parentReward = location.state?.parentReward || null;
  const userDetails = location.state?.userDetails || null;

  const showModal = (teamMembers: string) => {
    setModalContent(teamMembers.replace(/~/g, '\n')); // Replace `~` with line breaks for better readability
    setIsModalVisible(true);
  };

  const handleOk = () => {
    setIsModalVisible(false);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };
  useEffect(() => {
    if (rewardDetails.length > 0) {
      const normalizedUserRoles = userDetails?.map((role: string) =>
        role.replace(/\s+/g, '').trim().toLowerCase()
      );

      setUserRoles(normalizedUserRoles || []); // Set user roles once, outside the loop

      const formattedCards = rewardDetails
        .map((reward: any) => {
          const normalizedRewardRoles = reward.rolesList.map((r: string) =>
            r.replace(/\s+/g, '').trim().toLowerCase()
          );

          // Check if the reward has any special restriction (e.g., "notVisibleToAllUsers")
          const hasSpecialRestriction = normalizedRewardRoles.includes('notvisibletoallusers');

          // Determine if the card is enabled
          const isEnabled =
            Array.isArray(normalizedUserRoles) &&
            Array.isArray(normalizedRewardRoles) &&
            (!hasSpecialRestriction
              ? normalizedUserRoles.some((role: string) => normalizedRewardRoles.includes(role))
              : normalizedUserRoles.some((role: string) => normalizedRewardRoles.includes(role)));

          // If the card is not enabled, return null to filter it out later
          if (!isEnabled) {
            return null;
          }

          return {
            icon: (
              <div className="w-20 h-20 md:w-24 md:h-24 flex items-center justify-center">
                <img
                  src={`data:image/svg+xml;base64,${reward.icon}` || '/default-icon.svg'} // Use a default icon if `icon` is null
                  alt={reward.rewardName}
                  className="w-8 h-8 object-contain"
                />
              </div>
            ),
            title: reward.rewardName,
            details: reward,
            isEnabled,
          };
        })
        .filter((card: CardType | null): card is CardType => card !== null)
      setCards(formattedCards); // Set only the filtered cards
    }
  }, [rewardDetails, userDetails]);
  const fetchToken = async () => {
    const res = await instance.acquireTokenSilent({
      ...loginRequest,
      account: accounts[0],
    });
    return res.idToken;
  };

  const fetchNominations = async () => {
    try {
      const token = await fetchToken();
      const baseURL = process.env.REACT_APP_API_URL;
      const userId = localStorage.getItem("UserId");
      if (!userId) {
        console.log('Manager ID not found in localStorage');
        return; // Exit if managerId is not available
      }
      console.log('User ID:', userId); // Log the userId for debugging
      const response = await axios.get(`${baseURL}/api/RewardNomination/getRewardNominationByUserId`, {
        params: { userId },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.status === 200) {
        const sortedNominations = response.data.sort(
          (a: any, b: any) => new Date(b.rewardDate).getTime() - new Date(a.rewardDate).getTime()
        );
        setNominations(sortedNominations);
      }
    } catch (error) {
      console.error('Error fetching nominations:', error);
    }
  };



  const handleCardClick = async (title: string) => {
    if (navigating) return; // block double-clicks while a selection is in flight
    const selectedReward = cards.find((card) => card.title === title);
    if (selectedReward) {
      setNavigating(true);
      try {
        const token = await fetchToken();
        const baseURL = process.env.REACT_APP_API_URL;

        // Fetch child rewards using the ByParentId API
        const response = await axios.get(`${baseURL}/api/Rewards/ByParentId/${selectedReward.details.rewardTypeId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          timeout: 10000,
        });

        if (response.status === 200) {
          const childRewards = response.data;

          if (childRewards.length === 0) {
            // No child rewards, navigate with the current reward details
            const { icon, ...serializableParentReward } = selectedReward; // Remove non-serializable data
            navigate('/ApteanAwardsSection', {
              state: {
                rewardDetails: [selectedReward.details], // Wrap in an array for consistency
                parentReward: serializableParentReward,
                preParentReward: parentReward, // Pass the parent reward if needed
                userRoles: userRoles,
              },
            });
          } else {
            // Child rewards found, navigate with the child rewards
            const { icon, ...serializableParentReward } = selectedReward; // Remove non-serializable data
            navigate('/ApteanAwardsSection', {
              state: {
                rewardDetails: childRewards,
                parentReward: serializableParentReward,
                preParentReward: parentReward, // Pass the parent reward if needed
                userRoles: userRoles,

              },
            });
          }
        }
      } catch (error) {
        console.error('Error fetching child rewards:', error);
        setNavigating(false); // reset so the user can retry
      }
    }
  };

  const handleMenuClick = (section: string) => {
    setSelectedSection(section);
    if (section === 'nominated') {
      fetchNominations(); // Fetch nominations when "Nominated by You" is clicked
    }
  };
  const capitalizeWords = (text: string) => {
    return text
      .toLowerCase()
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };
  return (
    <Layout style={{ background: '#F9FAFB' }}>
      <AppHeaderHomePageTemplate />
      <Layout style={{ padding: '0 24px 0px' }}>
        <Breadcrumb style={{ margin: '10px 0' }}>
          <Breadcrumb.Item onClick={() => navigate('/home')}>
            <div style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
              <HomeOutlined />
              <span style={{ marginLeft: '4px' }}>{t('home')}</span>
            </div>
          </Breadcrumb.Item>
          <Breadcrumb.Item>{parentReward?.title || t('rewards')}</Breadcrumb.Item>
        </Breadcrumb>
        <Content
          style={{
            minHeight: 280,
            background: '#fff',
            borderRadius: '8px',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
            position: 'relative',
          }}
        >
          {navigating && (
            <div style={{
              position: 'absolute', inset: 0, zIndex: 10,
              background: 'rgba(255,255,255,0.65)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              borderRadius: '8px',
            }}>
              <Spin size="large" tip={t('loading', { defaultValue: 'Loading…' })} />
            </div>
          )}
          <div style={{ borderBottom: '1px solid #E5E7EB', marginBottom: '24px' }}>

            <div style={{ display: 'flex', gap: '32px', justifyContent: 'center' }}>
              <div
                onClick={() => handleMenuClick('awards')}
                style={{
                  padding: '8px 14px',
                  cursor: 'pointer',
                  borderBottom: selectedSection === 'awards' ? '2px solid #8B5CF6' : 'none',
                  color: selectedSection === 'awards' ? '#8B5CF6' : '#6B7280',
                  fontWeight: selectedSection === 'awards' ? 600 : 500,
                  fontSize: '18px',
                  transition: 'all 0.2s ease'
                }}
              >
                {t('awards')}
              </div>
              <div
                onClick={() => handleMenuClick('nominated')}
                style={{
                  padding: '12px 8px',
                  cursor: 'pointer',
                  borderBottom: selectedSection === 'nominated' ? '2px solid #8B5CF6' : 'none',
                  color: selectedSection === 'nominated' ? '#8B5CF6' : '#6B7280',
                  fontWeight: selectedSection === 'nominated' ? 600 : 500,
                  fontSize: '16px',
                  transition: 'all 0.2s ease'
                }}
              >
                {t('nominated_by_you')}
              </div>
            </div>
          </div>

          {/* Conditional Rendering Based on Selected Section */}
          {selectedSection === 'awards' && (
            <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '24px', paddingBottom: '40px' }}>
              {cards.map((card, index) => (
                <div
                  key={index}
                  style={{
                    borderRadius: '12px',
                    overflow: 'hidden',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
                    border: '1px solid #E5E7EB',
                    padding: '20',
                    cursor: card.isEnabled ? 'pointer' : 'not-allowed',
                    opacity: card.isEnabled ? 1 : 0.5,
                  }}
                  onClick={() => card.isEnabled && handleCardClick(card.title)}
                >
                  <ActionCardCatagories
                    icon={card.icon}
                    title={card.title}
                    message={''}
                    background={'#68C4D8 '}
                    border={'1px solid #68C4D8'}
                    variant="default"
                  />
                </div>
              ))}
            </div>
          )}


          {selectedSection === 'nominated' && (
            <div style={{ padding: '20px' }}>
              {nominations.length > 0 ? (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <colgroup>
                    <col style={{ width: '10%' }} /> {/* Award Name */}
                    <col style={{ width: '10%' }} /> {/* Date */}
                    <col style={{ width: '10%' }} /> {/* Type */}
                    <col style={{ width: '10%' }} /> {/* Value Name */}
                    <col style={{ width: '10%' }} /> {/* Team Name */}
                    <col style={{ width: '10%' }} /> {/* Nominee */}
                    <col style={{ width: '40%' }} />   {/* Citation */}
                  </colgroup>
                  <thead>
                    <tr>
                      <th style={{ border: '1px solid #E5E7EB', padding: '8px', background: '#F3F4F6' }}>Award Name</th>
                      <th style={{ border: '1px solid #E5E7EB', padding: '8px', background: '#F3F4F6' }}>Date</th>
                      <th style={{ border: '1px solid #E5E7EB', padding: '8px', background: '#F3F4F6' }}>Type</th>
                      <th style={{ border: '1px solid #E5E7EB', padding: '8px', background: '#F3F4F6' }}>Value</th>
                      <th style={{ border: '1px solid #E5E7EB', padding: '8px', background: '#F3F4F6' }}>Team Name</th>
                      <th style={{ border: '1px solid #E5E7EB', padding: '8px', background: '#F3F4F6' }}>Nominee</th>
                      <th style={{ border: '1px solid #E5E7EB', padding: '8px', background: '#F3F4F6' }}>Citation</th>
                    </tr>
                  </thead>
                  <tbody>
                    {nominations.map((nomination, index) => (
                      <tr key={index}>
                        <td style={{ border: '1px solid #E5E7EB', padding: '8px' }}>
                          {capitalizeWords(nomination.rewardName)}
                        </td>
                        <td style={{ border: '1px solid #E5E7EB', padding: '8px' }}>
                          {new Date(nomination.rewardDate).toLocaleDateString()}
                        </td>
                        <td style={{ border: '1px solid #E5E7EB', padding: '8px' }}>
                          {capitalizeWords(nomination.type)}
                        </td>
                        <td style={{ border: '1px solid #E5E7EB', padding: '8px' }}>
                          {capitalizeWords(nomination.valueName)}
                        </td>
                        <td style={{ border: '1px solid #E5E7EB', padding: '8px' }}>
                          {nomination.teamName ? capitalizeWords(nomination.teamName) : 'N/A'}
                        </td>
                        <td style={{ border: '1px solid #E5E7EB', padding: '8px' }}>
                          {nomination.type === 'individual' ? (
                            `${capitalizeWords(nomination.nomineeName)} (${nomination.nomineeEmail})`
                          ) : (
                            <Button
                              type="link"
                              onClick={() => showModal(nomination.teamMembers)}
                              style={{ padding: 0, color: '#8B5CF6' }}
                            >
                              View Team Members
                            </Button>
                          )}
                        </td>
                        <td style={{ border: '1px solid #E5E7EB', padding: '8px' }}>
                          {nomination.citation ? (
                            <>
                              {capitalizeWords(nomination.citation)} <br />
                              {nomination.formFields && `${capitalizeWords(nomination.formFields)}`}
                            </>
                          ) : 'N/A'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p style={{ textAlign: 'center', color: '#6B7280' }}>No nominations found.</p>
              )}

              {/* Modal for displaying team members */}
              <Modal
                title="Team Members"
                visible={isModalVisible}
                onOk={handleOk}
                onCancel={handleCancel}
                width={600}
                footer={[
                  <Button key="ok" type="primary" onClick={handleOk}>
                    OK
                  </Button>,
                ]}
              >
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(270px, 1fr))', // Responsive grid
                    gap: '10px',
                  }}
                >    {modalContent.split('\n').map((member, index) => {
                  const [email, name] = member.split(' ('); // Split email and name
                  const displayName = name ? name.replace(')', '') : ''; // Remove closing parenthesis
                  return (
                    <div
                      key={index}
                      style={{
                        padding: '10px',
                        border: '1px solid #E5E7EB',
                        borderRadius: '8px',
                        background: '#F9FAFB',
                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                        fontSize: '14px',
                        color: '#374151',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                      title={`${displayName}\n${email}`} // Tooltip on hover
                    >
                      <div style={{ fontWeight: 'bold', marginBottom: '4px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {displayName}
                      </div>
                      <div style={{ color: '#6B7280', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {email}
                      </div>
                    </div>
                  );
                })}
                </div>
              </Modal>
            </div>
          )}
        </Content>
      </Layout>
    </Layout>
  );
};

export default AwardsPage;